/*#######################################
              Importaciones
#########################################*/
// Módulos
import React, { lazy, Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Estilos
import './index.css';

// Componentes
import NavBar from './components/navbar/NavBar';
import FooterContainer from './components/footerContainer/FooterContainer';
import Home from './components/home/Home';
import Contact from './components/contact/Contact';
import WhatsAppFloat from './components/whatsapp/WhatsAppFloat';
import { AdminRoute } from './components/ProtectedRoute';
import MaintenanceScreen, { useMaintenance } from './components/maintenance/MaintenanceScreen';

import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';

// Core
import reportWebVitals from './reportWebVitals';

/*#######################################
   Auto-reload ante chunks viejos
#########################################*/
// Tras un deploy nuevo, el index.html cacheado puede pedir chunks con hashes
// viejos que ya no existen en el server (404). React lanza "Loading chunk X
// failed" / "ChunkLoadError". Detectamos ese caso y forzamos una recarga.
// Usamos sessionStorage para evitar loops de recarga infinita.
const isChunkLoadError = (msg = "") => {
  const m = String(msg);
  return m.includes("Loading chunk") ||
         m.includes("ChunkLoadError") ||
         m.includes("Loading CSS chunk");
};
const handleChunkError = () => {
  if (sessionStorage.getItem("chunk-reload") === "1") return;
  sessionStorage.setItem("chunk-reload", "1");
  // Limpiar caches del SW si existen, luego recargar sin caché
  if ("caches" in window) {
    caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k))))
      .finally(() => window.location.reload());
  } else {
    window.location.reload();
  }
};
window.addEventListener("error", (e) => {
  if (isChunkLoadError(e?.message) || isChunkLoadError(e?.error?.message)) {
    handleChunkError();
  }
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = e?.reason?.message || e?.reason?.toString?.() || "";
  if (isChunkLoadError(msg)) handleChunkError();
});
// Si la navegación termina bien, limpiar el flag para permitir un futuro reload
window.addEventListener("load", () => {
  setTimeout(() => sessionStorage.removeItem("chunk-reload"), 5000);
});

// Lazy load: rutas pesadas o poco usadas en la primera carga
const ItemListContainer = lazy(() => import('./components/itemListContainer/ItemListContainer'));
const ItemDetailContainer = lazy(() => import('./components/itemDetailContainer/ItemDetailContainer'));
const Checkout = lazy(() => import('./components/checkout/Checkout'));
const LoginPage = lazy(() => import('./components/login/LoginPage'));
const Unsubscribe = lazy(() => import('./components/unsubscribe/Unsubscribe'));
const Success = lazy(() => import('./components/mercadopagoext/Success'));
const Failure = lazy(() => import('./components/mercadopagoext/Failure'));
const Pending = lazy(() => import('./components/mercadopagoext/Pending'));
const ProductUploader = lazy(() => import('./components/ProductUploader'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const KitsManager = lazy(() => import('./components/admin/KitsManager'));
const ReturnsPolicy = lazy(() => import('./components/returnsPolicy/ReturnsPolicy'));

// Componente para scroll al top en cada cambio de ruta
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Componente para captar atención cuando el usuario sale de la pestaña
const TabAttentionGrabber = () => {
  useEffect(() => {
    const messages = [
      "¡Volvé! 👀",
      "¡No te lo pierdas! 🔥",
      "Te extrañamos 💔",
      "¡Mirá las ofertas! 🎁",
      "¡Hey, volvé! ⚡",
    ];
    
    let originalTitle = document.title;
    let messageIndex = 0;
    let intervalId = null;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Usuario salió de la pestaña - guardar título actual y empezar animación
        originalTitle = document.title;
        
        // Cambiar título inmediatamente
        document.title = messages[0];
        messageIndex = 1;
        
        // Rotar mensajes cada 2 segundos
        intervalId = setInterval(() => {
          document.title = messages[messageIndex];
          messageIndex = (messageIndex + 1) % messages.length;
        }, 2000);
      } else {
        // Usuario volvió - restaurar título y detener animación
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        document.title = originalTitle;
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);
  
  return null;
};

const AppLayout = () => {
  const location = useLocation();
  const isBackofficeRoute =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/astericimg');

  // Modo mantenimiento (kill switch). Solo bloquea rutas públicas — el admin,
  // login y unsubscribe siguen accesibles para poder desactivarlo.
  const maintenance = useMaintenance();
  const isExemptRoute =
    isBackofficeRoute ||
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/unsubscribe');
  if (maintenance.on && !isExemptRoute) {
    return <MaintenanceScreen message={maintenance.message} />;
  }

  return (
    <div className="app-container">
      <ScrollToTop />
      <TabAttentionGrabber />
      <NavBar />
      <Suspense fallback={<div>Cargando productos...</div>}>
        <Routes>
          <Route path="/" element={<Home greeting="Techteno ⚡ Tecnología que te acompaña" />} />
          <Route path="/productos" element={<ItemListContainer />} />
          <Route path="/productos/:productoId" element={<ItemDetailContainer />} />
          <Route path="/contacto" element={<Contact />} />
          <Route path="/devoluciones" element={<ReturnsPolicy />} />
          <Route path="/astericimg" element={
            <AdminRoute>
              <ProductUploader />
            </AdminRoute>
          } />
          <Route path="/admin" element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />
          <Route path="/admin/kits" element={
            <AdminRoute>
              <KitsManager />
            </AdminRoute>
          } />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/success" element={<Success />} />
          <Route path="/failure" element={<Failure />} />
          <Route path="/pending" element={<Pending />} />
          <Route
            path="/carrito"
            element={<Checkout />}
          />
          <Route
            path="/checkout"
            element={<Checkout />}
          />
        </Routes>
      </Suspense>
      {!isBackofficeRoute && <WhatsAppFloat />}
      {!isBackofficeRoute && <FooterContainer />}
    </div>
  );
};

/*#######################################
                Componente App
#########################################*/
const App = () => {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <AppLayout />
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

/*#######################################
                Renderizado
#########################################*/
// Soporte para react-snap: si el contenido ya está pre-renderizado en HTML
// (build estático generado por react-snap) hidratamos en lugar de renderizar
// desde cero. Esto mejora SEO porque Googlebot ve HTML real al cargar.
const rootElement = document.getElementById('root');
if (rootElement.hasChildNodes()) {
  ReactDOM.hydrateRoot(
    rootElement,
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
} else {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

// Reporte de métricas
reportWebVitals();
